import { Auth } from '@aws-amplify/auth';
import { ConsoleLogger as Logger } from '@aws-amplify/core';
import { ConfirmSignUp } from 'aws-amplify-react';
import { createRef } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import VerificationInput from 'react-verification-input';

import { BrandContext } from '../context/BrandProvider';
import withHookedTheme from '../hocs/withHookedTheme';
import type { Theme } from '../theme/themes/ThemeTypes';
import LoginLayout from '../ui/Layout/LoginLayout';
import LoginCard from '../ui/LoginCard';
import {
  buildRedirectUrl as buildDomainRedirectUrl,
  getCurrentHostPrefix as getHostPrefix,
  hasRedirected,
} from '../utils/domainRedirect';

const logger = new Logger('ConfirmSignUp');

type BrandCtxValue = {
  domainPrefix?: string;
  installerId?: string;
  brandResolving?: boolean;
};

interface Props {
  theme: {
    language: {
      appName: string;
    };
  };
}

interface State {
  resentEmail: boolean;
  entryComplete: boolean;
}

/**
 * Amplify confirm signup screen
 */
class LoopConfirmSignUp extends ConfirmSignUp {
  language: any;
  _validAuthStates: string[];
  state: State;
  validationInputRef: any;
  inputs: any;

  /**
   * Class constructor
   * @param {Object} props Properties passed to this screen
   */
  constructor(props: Props) {
    super(props);
    this.language = props.theme.language;
    this._validAuthStates = ['confirmSignUp'];
    this.onKeyUp = this.onKeyUp.bind(this);

    this.state = {
      resentEmail: false,
      entryComplete: false,
    };

    this.validationInputRef = createRef();
  }

  // Access BrandProvider in a class component
  static contextType = BrandContext;

  /**
   * onKeyUp event handler
   * @param {Object} event The onKeyUp event
   */
  onKeyUp(event: any) {
    if (event.charCode === 13) {
      this.confirm();
    }
  }

  /**
   * validate event handler
   * @param {Object} event The validate event
   */
  validate(event: any) {
    const { value } = event.nativeEvent.target;
    this.setState({ entryComplete: value.length === 6 });
    this.handleInputChange(event);
  }

  /**
   * Reset the input field and validate the input component
   */
  resetInputAndValidationInputComponent() {
    this.inputs.code = 0;
    this.validationInputRef.current.state.tan = '';
    this.validationInputRef.current.state.previousTan = '';
    this.validationInputRef.current.state.selectedIndex = 0;
    this.validationInputRef.current.state.isActive = false;
    this.validationInputRef.current.state.isValidTan = false;
  }

  /**
   * Resend the sign up, so user is sent new code
   */
  resend() {
    const username = this.usernameFromAuthData() || this.inputs.username;
    if (!Auth || typeof Auth.resendSignUp !== 'function') {
      throw new Error('No Auth module found, please ensure @aws-amplify/auth is imported');
    }
    Auth.resendSignUp(username)
      .then(() => {
        logger.debug('code resent');
        // this is to clear any error, not ideal as get an empty red bar
        this.error('');
        this.setState({ resentEmail: true });
      })
      .catch((err: any) => {
        this.error(err);
        this.setState({ resentEmail: false });
      });
  }

  /**
   * Confirm sign up method
   */
  confirm() {
    const username = this.usernameFromAuthData() || this.inputs.username;
    const code = this.inputs.code;

    if (!username || !code) {
      this.error('Please enter your email and verification code.');
      return;
    }

    Auth.confirmSignUp(username, code)
      .then(() => {
        const brandCtx = this.context as BrandCtxValue;
        const expectedPrefix = brandCtx?.domainPrefix;
        const currentPrefix = getHostPrefix();

        if (expectedPrefix && !brandCtx?.brandResolving) {
          // If we've already been redirected (query param present), do not redirect again
          if (!hasRedirected() && expectedPrefix !== currentPrefix) {
            window.location.href = buildDomainRedirectUrl(expectedPrefix, '/login', { redirected: 'confirmSignUp' });
            return;
          }
        }

        super.changeState('signIn');
      })
      .catch((err: unknown) => {
        this.error(typeof err === 'string' ? err : (err as Error)?.message || 'An error occurred');
      });
  }

  /**
   * Main render method for this component
   * @param {Object} theme The theme configuration
   * @return {JSX.Element}
   */
  showComponent(theme: Theme): JSX.Element {
    const username = this.usernameFromAuthData();
    const { resentEmail, entryComplete } = this.state;
    const onKeyUp = (e: React.KeyboardEvent) => this.onKeyUp(e);

    return (
      <LoginLayout>
        <LoginCard>
          <Card.Title as="h1">Verify your email</Card.Title>
          {username && (
            <Card.Text>
              We sent a six digit verification code to:
              <span className="py-1 d-block"></span>
              <strong>{username}</strong>
              <span className="py-1 d-block"></span>
              Keep this window open while you check your inbox, then enter the code below.
              <span className="py-1 d-block"></span>
              If you don&apos;t see the email, you may need to check your spam folder, and ensure you&apos;ve typed in
              your email address correctly.
            </Card.Text>
          )}
          {!username && (
            <Card.Text>
              Please enter the email address that we sent your six digit verification code to, then enter the six digit
              code below.
              <span className="py-1 d-block"></span>
              If you don&apos;t see the email, you may need to check your spam folder, and ensure you&apos;ve typed in
              your email address correctly.
            </Card.Text>
          )}
          <Form
            className="text-left mt-2"
            onSubmit={(e: any) => {
              e.preventDefault();
              this.confirm();
              return false;
            }}
          >
            {!username && (
              <Form.Group>
                <Form.Label htmlFor="username">Email address</Form.Label>
                <Form.Control
                  id="confirm_signup_username"
                  key="username"
                  name="username"
                  onChange={(e: any) => {
                    e.target.value = e.target.value.toLowerCase().trim();
                    this.handleInputChange(e);
                  }}
                  type="email"
                  placeholder="Email address"
                  onKeyPress={onKeyUp}
                />
              </Form.Group>
            )}
            <Form.Group className="mb-2">
              <Form.Label htmlFor="username">Six digit verification code</Form.Label>
              <VerificationInput
                ref={this.validationInputRef}
                validChars="0-9"
                placeholder="0"
                removeDefaultStyles={true}
                inputField={{
                  name: 'code',
                  onChange: (event: any) => this.validate(event),
                }}
                container={{
                  className: 'verification-input-container',
                }}
                characters={{
                  className: 'verification-input-characters',
                }}
                character={{
                  className: 'verification-input-character',
                  classNameInactive: 'verification-input-character--inactive',
                  classNameSelected: 'verification-input-character--selected',
                }}
                autoFocus={!!username}
              />
            </Form.Group>
            <Form.Group className="mb-0">
              {resentEmail ? (
                <p className="mb-0">Email resent</p>
              ) : (
                <p className="mb-0">
                  Didn&apos;t get the code?{' '}
                  <Button
                    variant="inline-link"
                    onClick={() => {
                      this.resend();
                      this.resetInputAndValidationInputComponent();
                    }}
                  >
                    Resend code
                  </Button>
                </p>
              )}
            </Form.Group>
            <Form.Group>
              <Button type="submit" variant="primary" block disabled={!entryComplete}>
                Submit
              </Button>
            </Form.Group>
          </Form>
          <div>
            <div className="or-panel">
              <hr />
              <span className="or-text">or</span>
              <hr />
            </div>

            <p className="mb-2">
              <Button
                variant="link"
                onClick={() => {
                  super.changeState('signIn');
                  this.resetInputAndValidationInputComponent();
                }}
              >
                Go back and try a different email address
              </Button>
            </p>
            <p className="mb-0">
              Still no email?{' '}
              <a href={theme.links.cantVerifyEmailSupport} target="_blank" rel="noreferrer">
                Go to {this.language.appName} Support
              </a>
            </p>
          </div>
        </LoginCard>
      </LoginLayout>
    );
  }
}

export default withHookedTheme(LoopConfirmSignUp);
